import compact from 'lodash/compact'
import filter from 'lodash/filter'
import find from 'lodash/find'
import indexOf from 'lodash/indexOf'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import omitBy from 'lodash/omitBy'
import reduce from 'lodash/reduce'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'

import { getColor } from '@Components/Chips/DiscountChip'
import { ProductPersonalization } from '@Components/Personalization/Modals/Shared'
import { ProductTileProps } from '@Domains/Product/Tiles/types'
import {
  Attribute,
  BaseAttribute,
  Maybe,
  Product,
  ProductContentFragment,
  ProductTileFragmentFragment,
  ProductVariant,
  ProductWithQuantity,
  Scalars,
} from '@Generated/graphql'
import { AlgoliaProductTile, AlgoliaProductV2 } from '@Globals/Algolia/Types'
import { giftWrappingIDs } from '@Shared/Statics/GiftWrappings'

const isBaseAttribute = (attribute: Attribute | undefined):
  attribute is BaseAttribute => attribute?.__typename === 'BaseAttribute'

export const getBaseAttribute = <T extends Scalars['JSON']>(attributes: Attribute[], key: string): Maybe<T> => {
  const attribute = attributes.find(x => x.key === key)
  return isBaseAttribute(attribute) ? attribute.value as T : null
}

export const getPersonalizationsFromProduct = ({ masterVariant }: Product): ProductPersonalization[] => (
  // @ts-ignore
  masterVariant?.personalizations?.products as ProductPersonalization[] || []
)

export const getPersonalizationTypes = (items: ProductPersonalization[]): string[] => compact(uniq(map(items, 'masterVariant.type.value.key')))

export const cmsProductToGridProduct = (
  product: Product,
  showBuyButton: boolean = false,
): ProductTileProps | undefined => {
  // TODO: Consider checking on more attributes
  // When no price is available for a product, there's no need to render it
  if (!product?.masterVariant?.price?.format) {
    return undefined
  }

  const normalPrice = product.masterVariant.price
  const price = product.masterVariant.price.discount ?? normalPrice
  // TODO: Get this from graph instead
  const discountPercentage = product.masterVariant.price.discount?.percent ?? 0
  // TODO: Refactor to use query-specific product data, to make primaryAsset available directly.
  // Currently we use type hints that does not match what we query, which hinders a lot of what graph gives us.
  // No custom resolvers on a product is available currently.
  const primaryAsset = find(product.assets, asset => indexOf(asset.tags || [], 'primary') !== -1) || product.assets?.[0]
  const primaryImage = primaryAsset?.url

  const isInStock = product.availability?.isInStock
  const quantity = product.availability?.quantity || 0
  const isOutOfStock = !isInStock && quantity <= 0

  const discountContext = product.masterVariant?.price?.discount?.code?.context

  return {
    beforePrice: (product.masterVariant.price.discount && normalPrice.format) || undefined,
    brandTitle: product.brand?.title || '',
    discount: (discountPercentage || discountContext)
      ? ({
        percentage: discountPercentage,
        ...discountContext && { context: discountContext },
      })
      : undefined,
    id: product.id,
    isOutOfStock,
    price: price.format || '',
    primaryImage,
    showBuyButton,
    title: product.name || '',
    url: product.url || '',
    // TODO: resolve variantsColorCount from the product query
    variantsColorCount: 0,
  }
}

// @ts-ignore - "product.bundle" doesn't exist in the auto-generated types, but it's in the query.
export const getBundledProductList = (product?: Product): ProductWithQuantity[] => (product?.masterVariant?.bundleContent?.products || [])

export const getGalleryAssetsFromProduct = (product?: Product): Product['assets'] => filter(
  product?.assets,
  asset => asset.tags?.includes('gallery'),
) as Product['assets']

export const getFirstVideoIndexInAssets = (assets?: Product['assets']) => assets?.findIndex(asset => asset.tags?.includes('video'))

export type CrossSellProduct =
  Pick<Product, 'name' | 'url' | 'id' | 'masterVariant'>
  & Pick<ProductContentFragment, 'primaryAsset'>

// @ts-ignore - ditto.
export const getCrossSellsForProduct = (product?: Product): CrossSellProduct[] => (product?.masterVariant?.crossSells?.products || [])

export function getTypeAndSubTypeForVariant(variant?: ProductVariant | null) {
  return {
    subType: getBaseAttribute<{ key?: string }>(variant?.attributes || [], 'subType')?.key || null,
    type: getBaseAttribute<{ key?: string }>(variant?.attributes || [], 'type')?.key || null,
  }
}

type GetAvailableVariantsProps = {
  includeVariantsThatWillNeverRestock?: boolean
  locale?: string
  product?: Product
}
export type SizeProps = {
  available?: boolean
  id: ProductVariant['id']
  key: string
  label: string
}

type FilteredVariantsReturnFormat = {
  availableVariants: SizeProps[]
  variantsInTotal: number
}

export const getAvailableVariantsForProduct = (
  {
    includeVariantsThatWillNeverRestock,
    locale,
    product,
  }: GetAvailableVariantsProps,
): FilteredVariantsReturnFormat => {
  if (!product) {
    return {
      availableVariants: [],
      variantsInTotal: 0,
    }
  }

  let variantsInTotal = 0
  const availableVariants = reduce<ProductVariant, SizeProps[]>(
    (product.variants || []) as ProductVariant[],
    (memo, item) => {
      const size: Record<string, any> | undefined = find(item.attributes, ['key', 'size'])
      if (!size) {
        return memo
      }

      const available = item.availability?.isInStock || false
      variantsInTotal += 1

      // If the product isn't available, we need to know if it ever will be again. If it isn't
      // due a restock at any time, there's no reason to show it to the user. They'll just be sad.
      // However, we might want to include the "dead" variants in certain cases, which is why we'll
      // do a double-check here.
      if (!available && !includeVariantsThatWillNeverRestock) {
        const restock: Record<string, any> | undefined = find(item.attributes, ['key', 'restock'])

        if (restock?.value?.key !== 'enabled') {
          return memo
        }
      }

      return [
        ...memo,
        {
          available,
          id: item.id,
          key: size?.value?.key || '',
          label: size?.value?.label?.[locale || 'en-GB'] || size?.value?.key || '',
        },
      ]
    },
    [],
  )

  return {
    availableVariants,
    variantsInTotal,
  }
}

const sortProductPersonalizations = (personalizations: ProductPersonalization[]) => {
  const preferredOrder = ['engrave', 'deboss']
  return sortBy(personalizations, p => preferredOrder.indexOf(p.masterVariant.type.value.key))
}

export const getUniqProductPersonalizations = (product: Product): ProductPersonalization[] => {
  const personalizations = getPersonalizationsFromProduct(product)
  return sortProductPersonalizations(uniqBy(personalizations, p => p.masterVariant.type.value.key))
}
export const getProductGiftWrapping = (product: Product): CrossSellProduct | undefined => {
  const crossSellsForProduct = getCrossSellsForProduct(product)
  return crossSellsForProduct.find(cs => giftWrappingIDs.includes(cs.id))
}

export const getProductCrossSellGroups = (product: Product): {
  crossSells: CrossSellProduct[]
  giftWrappings: CrossSellProduct[]
  personalizations: ProductPersonalization[]
} => {
  const personalizations = getUniqProductPersonalizations(product)
  const giftWrapping = getProductGiftWrapping(product)
  const crossSells = getCrossSellsForProduct(product)
    .filter(cs => cs.id !== giftWrapping?.id)
  return {
    crossSells,
    giftWrappings: giftWrapping ? [giftWrapping] : [],
    personalizations,
  }
}

export const getPersonalization = (item: any) => ({
  id: item.id || '',
  price: item.masterVariant?.price?.format || '',
  discountedPrice: item.masterVariant?.price?.discount?.format || '',
  isFree: item.masterVariant?.price?.value === 0 || item.masterVariant?.price?.discount?.value === 0 || false,
  image: item.primaryAsset?.url || item.asset?.url || '',
  placement: item.masterVariant?.shortName?.value || {},
  maxCharacters: item.masterVariant?.maxCharacters?.value,
  type: item.masterVariant?.type?.value.key,
})

function isAlgoliaProductV2(input: unknown): input is AlgoliaProductV2 {
  if (typeof input !== 'object' || input == null) {
    return false
  }

  return 'objectID' in input && typeof input.objectID === 'string'
}

function isProductTileFragment(input: unknown): input is ProductTileFragmentFragment {
  if (typeof input !== 'object' || input == null) {
    return false
  }
  if (!('masterVariant' in input) || typeof input.masterVariant !== 'object' || input.masterVariant == null) {
    return false
  }
  if (!('isNew' in input.masterVariant)) {
    return false
  }
  return true
}

export const getChipsV2 = (product?: Product | AlgoliaProductV2): { bottomChips: any[], topChips: any[] } => {
  if (!product) {
    return {
      bottomChips: [],
      topChips: [],
    }
  }

  let assortmentLadderTag = ''
  let isBestSeller = false
  let isNew = false
  let personalizable = false
  let personalizableEngraveAvailable = false
  let productDiscountAvailable = false
  let productDiscountContext = ''
  let productDiscountPercentage = 0

  if (isAlgoliaProductV2(product)) {
    assortmentLadderTag = product.assortmentLadder?.tag || ''
    isBestSeller = Boolean(product.isBestSeller)
    isNew = Boolean(product.isNew)
    personalizable = Array.isArray(product.personalizationTypes) && product.personalizationTypes.length !== 0
    personalizableEngraveAvailable = personalizable && product.personalizationTypes.includes('engrave')
    productDiscountAvailable = `${product.objectID}` !== '26342' && Boolean(product.displayPrice?.discount)
    productDiscountContext = product.displayPrice?.discount?.code?.context || ''
    productDiscountPercentage = product.displayPrice?.discount?.percent || 0
  } else if (isProductTileFragment(product)) {
    if (product?.masterVariant?.isBestSeller && 'value' in product.masterVariant.isBestSeller) {
      isBestSeller = Boolean(product.masterVariant.isBestSeller.value)
    }

    if (product?.masterVariant?.isNew && 'value' in product.masterVariant.isNew) {
      isNew = Boolean(product.masterVariant.isNew.value)
    }

    if (product?.masterVariant?.personalizations
      && 'productIDs' in product.masterVariant.personalizations
      && Array.isArray(product.masterVariant.personalizations.productIDs)
    ) {
      personalizable = product.masterVariant.personalizations.productIDs.length !== 0
    }

    productDiscountAvailable = `${product.id}` !== '26342' && Boolean(product.masterVariant?.price?.discount)
    productDiscountContext = product.masterVariant?.price?.discount?.code?.context || ''
    productDiscountPercentage = product.masterVariant?.price?.discount?.percent || 0
  } else {
    isBestSeller = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isBestSeller') || false
    isNew = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isNew') || false
    personalizable = !!product?.masterVariant?.attributes?.find(x => x.key === 'personalizations')
    productDiscountAvailable = `${product.id}` !== '26342' && Boolean(product.masterVariant?.price?.discount)
    productDiscountContext = product.masterVariant?.price?.discount?.code?.context || ''
    productDiscountPercentage = product.masterVariant?.price?.discount?.percent || 0
  }

  return {
    bottomChips: compact([
      assortmentLadderTag && {
        color: 'greyV3',
        // TODO: re-add icon when we have fixed sizing issues
        // icon: product.assortmentLadder?.asset?.url && product.assortmentLadder.asset.dimensions?.width ? product.assortmentLadder.asset : undefined,
        text: assortmentLadderTag,
      },
      personalizableEngraveAvailable && {
        color: 'greyV3',
        translation: ['products:ENGRAVE_LABLE'],
      },
      personalizable && !personalizableEngraveAvailable && {
        color: 'greyV3',
        translation: ['common:PERSONALISABLE_PRODUCT_TAG'],
      },
    ]),
    topChips: compact([
      productDiscountAvailable && {
        color: getColor(productDiscountContext),
        translation: [
          'common:discount_chip',
          {
            context: productDiscountContext,
            discountPercentage: productDiscountPercentage,
          },
        ],
      },
      isNew && !isBestSeller && {
        color: 'blue',
        translation: ['common:BADGE_NEW_LABEL'],
      },
      isBestSeller && !isNew && {
        color: 'blue',
        translation: ['common:WB_BESTSELLERS_LABEL', { count: 1 }],
      },
    ]),
  }
}

export const algoliaProductToProductTile = (product: AlgoliaProductV2, boosted?: boolean): AlgoliaProductTile => omitBy<AlgoliaProductTile>({
  ...getChipsV2(product),
  // TODO: eventually images will be guaranteed in the index, and we can simplify the mapping here
  assetURLs: compact(
    product.assets && product.assets.length !== 0
      ? map(product.assets, asset => (asset.url || null))
      : [product.primaryAsset.url || null, product.hoverAsset.url || null],
  ),
  beforePrice: product.displayPrice?.discount ? product.displayPrice.format : undefined,
  boosted,
  brandTitle: product.brand,
  bundleCombinedValue: product.bundle?.combinedValue.format,
  category: product.categoryPath,
  discount: product.displayPrice?.discount && {
    context: product.displayPrice.discount.code?.context || '',
    percentage: product.displayPrice?.discount?.percent || 0,
  },
  groupID: product.groupID || null,
  id: product.objectID,
  isOutOfStock: !product.isInStock,
  price: product.displayPrice?.discount ? product.displayPrice.discount.format : product.displayPrice?.format,
  priceValue: product.displayPrice?.discount ? product.displayPrice.discount.plain : product.displayPrice?.plain || 0,
  primaryImage: product.primaryAsset.url,
  priority: false,
  hoverImage: product.hoverAsset.url,
  sourceID: 'inherit',
  title: product.title,
  url: product.slug,
  variantsColorCount: product?.variantsColorCount || 0,
}, isNil) as unknown as AlgoliaProductTile

export const productTileFragmentToProductTile = (product: ProductTileFragmentFragment): ProductTileProps => {
  const { bottomChips, topChips } = getChipsV2(product)

  return omitBy<ProductTileProps>({
    beforePrice: product.masterVariant?.price?.discount ? (product.masterVariant?.price?.format || '') : undefined,
    bottomChips,
    // TODO: fetch brand title
    brandTitle: undefined,
    // TODO: fetch bundle combined value
    bundleCombinedValue: undefined,
    discount: product.masterVariant?.price?.discount
      ? {
        context: product.masterVariant?.price.discount.code?.context || '',
        percentage: product.masterVariant?.price?.discount?.percent || 0,
      }
      : undefined,
    id: product.id,
    isOutOfStock: !product.availability?.isInStock,
    price: (product.masterVariant?.price?.discount ? product.masterVariant?.price.discount.format : product.masterVariant?.price?.format) || '',
    primaryImage: product.primaryAsset?.url || '',
    priority: false,
    hoverImage: product.hoverAsset?.url || '',
    sourceID: 'inherit',
    title: product.name || '',
    topChips,
    url: product.url || '',
    // TODO: fetch variants color count
    variantsColorCount: 0,
  }, isNil) as unknown as ProductTileProps
}

export const isGiftWrapping = (product: any) => reduce(
  product?.masterVariant?.bundleContent?.products || [],
  (memo, productWithQuantity) => {
    if (memo) {
      return memo
    }

    return productWithQuantity?.product?.type === 'giftWrappings'
  },
  product?.type === 'giftWrappings',
)
